import { ArrowPathRoundedSquareIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import {
  getEliteProgressStatus,
  getEliteValuationComments,
  postEliteValuationComments,
  postEliteValuationDocuments,
  syncOrderAppraisalsInfo,
} from 'services'
import { formatDate, formatTime } from 'utils'

import { AppraisalVendorModal } from './AppraisalVendorModal'

export const EliteValuation = ({ data, onClose }: { data: Record<string, any>; onClose: Function }) => {
  const [progress, setProgress] = useState<any>(null)
  const [syncLoading, setSyncLoading] = useState(false)

  const { OrderId, FileId } = data.thirdPartyAPI

  useEffect(() => {
    getEliteProgressStatus(OrderId).then((res) => {
      if (res.success) setProgress(res.data)
    })
  }, [])

  const diffDates = useMemo(() => {
    const rlt: any = {}
    if (progress?.sync) {
      Object.keys(progress.sync).forEach((key) => {
        if (formatDate(data.info[key]) !== formatDate(progress.sync[key])) {
          rlt[key] = formatDate(progress.sync[key])
        }
      })
    }
    return rlt
  }, [data, progress])

  const syncProgressDate = async () => {
    setSyncLoading(true)
    await syncOrderAppraisalsInfo({ id: data.id, dates: diffDates })
    setSyncLoading(false)
  }

  const renderProgressSection =
    progress !== null ? (
      <div className="px-2">
        <div className="mb-4">
          <div className="flex flex-wrap gap-4 justify-between">
            <div>
              Status: <span className="font-bold text-shade-blue italic">{progress.status}</span>
            </div>
            {Object.keys(diffDates).length > 0 && (
              <div
                className="ml-4 text-[15px] text-shade-blue cursor-pointer hover:underline italic flex flex-wrap gap-2 items-center"
                onClick={syncProgressDate}
              >
                Sync Progress Dates
                <ArrowPathRoundedSquareIcon className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-4 mb-4">
          {progress.dates.map((item: any, index: number) => (
            <div className="flex w-full flex-col sm:min-w-[270px] sm:w-auto sm:flex-row sm:items-center" key={index}>
              <div className="sm:min-w-[160px]">{item.label}:</div>{' '}
              <div className="italic font-bold text-[14px]">{formatDate(item.value)}</div>
            </div>
          ))}
        </div>
      </div>
    ) : null

  const renderProgressHeader = (
    <a
      href={`https://elitevaluations.myvalutrac.com/valutrac/admin/appraisals/modify.aspx?id=${OrderId}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className="flex flex-wrap gap-2 items-center rounded p-1 hover:underline cursor-pointer text-[14.5px] text-shade-blue">
        <span>Go to Elite Valuation Order</span>
        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
      </span>
    </a>
  )

  return (
    <AppraisalVendorModal
      title={`Elite Valuation - ${FileId || OrderId}`}
      data={data}
      onClose={onClose}
      fetchLogs={() => getEliteValuationComments(OrderId)}
      mapLogs={(res) =>
        res.data.map((item: any, index: number) => [
          index + 1,
          <div key={index}>
            {formatTime(item.NoteDate)}
            <br />
            <span className="italic">{item.UserName}</span>
          </div>,
          item.Message,
        ])
      }
      postComment={(comment) => postEliteValuationComments({ OrderId, comment })}
      postDocuments={(id, documents) => postEliteValuationDocuments({ id, OrderId, documents })}
      sendMessageLabel="Send Message to Elite Valuation"
      uploadDocumentsLabel="Upload Documents to Elite Valuation"
      renderProgressSection={renderProgressSection}
      renderProgressHeader={renderProgressHeader}
      extraLoading={syncLoading}
    />
  )
}
